import { useState } from "react";
import { Navbar, Footer } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { MapPin, Plus, X, Trash2 } from "lucide-react";
import { BackButton } from "@/components/BackButton";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { useJsonLd } from "@/hooks/useJsonLd";
import { useSeo } from "@/hooks/useSeo";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { MapContainer } from "react-leaflet/MapContainer";
import { TileLayer } from "react-leaflet/TileLayer";
import { Polygon } from "react-leaflet/Polygon";
import { LatLngBoundsExpression } from "leaflet";
import "leaflet/dist/leaflet.css";
import { format } from "date-fns";
import { sv } from "date-fns/locale";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

interface SavedArea {
  id: string;
  coordinates: [number, number][];
  area_sqm: number;
  area_ha: number;
  wood_type: string | null;
  created_at: string;
}

function MiniMap({ coordinates, height = "h-32" }: { coordinates: [number, number][]; height?: string }) {
  const positions = coordinates.map(([lng, lat]) => [lat, lng] as [number, number]);
  const bounds: LatLngBoundsExpression = positions.length > 0 ? positions as LatLngBoundsExpression : [[62, 15]];

  return (
    <div className={`${height} w-full overflow-hidden rounded-md minimap-card`} style={{ isolation: "isolate" }}>
      <MapContainer
        bounds={bounds}
        scrollWheelZoom={false}
        dragging={false}
        zoomControl={false}
        doubleClickZoom={false}
        touchZoom={false}
        attributionControl={false}
        style={{ height: "100%", width: "100%", position: "relative", zIndex: 0 }}
      >
        <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
        <Polygon
          positions={positions}
          pathOptions={{ color: "hsl(152, 45%, 28%)", fillColor: "hsl(152, 45%, 28%)", fillOpacity: 0.25, weight: 2 }}
        />
      </MapContainer>
    </div>
  );
}

function formatArea(sqm: number, ha: number) {
  if (ha >= 1) return `${ha.toFixed(2).replace(".", ",")} ha`;
  return `${Math.round(sqm).toLocaleString("sv-SE")} m²`;
}

export default function AreaCalculatorHub() {
  useSeo({
    title: "Areakalkylator — beräkna ytan på din skog",
    description: "Rita in ditt skogsområde på kartan och beräkna arean direkt. Spara dina mätningar och kom åt dem när du behöver.",
    path: "/skogsverktyg/arearaknare",
  });
  const navigate = useNavigate();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [selectedArea, setSelectedArea] = useState<SavedArea | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDelete = async () => {
    if (!selectedArea) return;
    setIsDeleting(true);
    const { error } = await supabase.from("saved_areas").delete().eq("id", selectedArea.id);
    setIsDeleting(false);
    if (error) {
      const { toast } = await import("sonner");
      toast.error("Kunde inte ta bort området");
      return;
    }
    setSelectedArea(null);
    queryClient.invalidateQueries({ queryKey: ["saved-areas", user?.id] });
  };

  const { data: areas = [], isLoading } = useQuery({
    queryKey: ["saved-areas", user?.id],
    queryFn: async () => {
      if (!user) return [];
      const { data, error } = await supabase
        .from("saved_areas")
        .select("*")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data as unknown as SavedArea[]) ?? [];
    },
    enabled: !!user,
  });

  useJsonLd(
    {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      name: "Areakalkylator",
      applicationCategory: "UtilitiesApplication",
      operatingSystem: "Web",
      url: "https://timbernet.se/skogsverktyg/arearaknare",
      description:
        "Interaktivt verktyg för att rita upp och beräkna arealer på karta — spara dina skogsområden och få ytan i hektar.",
      inLanguage: "sv-SE",
      offers: { "@type": "Offer", price: 0, priceCurrency: "SEK" },
      provider: { "@type": "Organization", name: "TimberNet", url: "https://timbernet.se" },
    },
    "area-calc-app"
  );

  return (
    <div className="flex min-h-screen flex-col">
      <Navbar />
      <div className="container flex-1 py-6">
        <BackButton fallback="/skogsverktyg" />

        {/* Info banner */}
        <div className="mb-6 rounded-xl border border-border bg-secondary p-6">
          <div className="flex items-start gap-4">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-primary/10">
              <MapPin className="h-6 w-6 text-primary" />
            </div>
            <div>
              <h1 className="mb-1 font-display text-xl font-bold md:text-2xl">Kartlägg ditt område</h1>
              <p className="text-sm text-muted-foreground">
                Rita ut ett område på kartan genom att placera punkter längs gränserna av ditt markområde.
                Punkterna kopplas ihop till en polygon och arean beräknas automatiskt. Du kan sedan spara
                området och koppla det till en annons.
              </p>
            </div>
          </div>
        </div>

        <Button
          size="lg"
          className="mb-8 w-full md:w-auto"
          onClick={() => navigate("/skogsverktyg/arearaknare/karta")}
        >
          <Plus className="mr-2 h-4 w-4" /> Kartlägg nytt område
        </Button>

        {/* Saved areas */}
        <h2 className="mb-4 font-display text-lg font-bold">Sparade områden</h2>

        {isLoading ? (
          <p className="text-sm text-muted-foreground">Laddar...</p>
        ) : areas.length === 0 ? (
          <p className="text-sm text-muted-foreground">Du har inga sparade områden ännu.</p>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2">
            {areas.map((area) => (
              <Card
                key={area.id}
                className="cursor-pointer overflow-hidden transition-shadow hover:shadow-card-hover"
                onClick={() => setSelectedArea(area)}
              >
                <div className="p-4">
                  <p className="font-display text-lg font-bold">
                    {formatArea(area.area_sqm, area.area_ha)}
                  </p>
                  <p className="mb-2 text-xs text-muted-foreground">
                    Skapad {format(new Date(area.created_at), "d MMMM yyyy", { locale: sv })}
                  </p>
                  {area.wood_type && (
                    <Badge variant="secondary" className="mb-3">
                      {area.wood_type}
                    </Badge>
                  )}
                </div>
                <MiniMap coordinates={area.coordinates} />
              </Card>
            ))}
          </div>
        )}
      </div>
      <Footer hideOnMobile />

      {/* Detail popup */}
      <Dialog open={!!selectedArea} onOpenChange={(open) => !open && setSelectedArea(null)}>
        <DialogContent className="max-h-[90vh] w-[90vw] max-w-2xl overflow-y-auto" style={{ zIndex: 1001 }}>
          <DialogHeader>
            <DialogTitle>Sparat område</DialogTitle>
          </DialogHeader>

          {selectedArea && (
            <div className="space-y-4">
              {/* Area stats */}
              <div className="rounded-md bg-secondary p-4">
                <p className="text-2xl font-bold text-primary">
                  {selectedArea.area_ha >= 1
                    ? `${selectedArea.area_ha.toFixed(2).replace(".", ",")} ha`
                    : `${Math.round(selectedArea.area_sqm).toLocaleString("sv-SE")} m²`}
                </p>
                <p className="text-sm text-muted-foreground">
                  {selectedArea.area_ha >= 1
                    ? `${Math.round(selectedArea.area_sqm).toLocaleString("sv-SE")} m²`
                    : `${selectedArea.area_ha.toFixed(4).replace(".", ",")} ha`}
                </p>
              </div>

              {/* Date */}
              <p className="text-sm text-muted-foreground">
                Skapad {format(new Date(selectedArea.created_at), "d MMMM yyyy", { locale: sv })}
              </p>

              {/* Wood type */}
              {selectedArea.wood_type && (
                <div>
                  <p className="text-xs font-medium text-muted-foreground">Trädslag</p>
                  <Badge variant="secondary">{selectedArea.wood_type}</Badge>
                </div>
              )}

              {/* Mini map */}
              <MiniMap coordinates={selectedArea.coordinates} height="h-56" />

              {/* Edit button */}
              <Button
                className="w-full"
                onClick={() => {
                  setSelectedArea(null);
                  navigate(`/skogsverktyg/arearaknare/karta/${selectedArea.id}`);
                }}
              >
                Redigera område
              </Button>

              {/* Delete button */}
              <Button
                variant="destructive"
                className="w-full"
                disabled={isDeleting}
                onClick={handleDelete}
              >
                <Trash2 className="mr-2 h-4 w-4" />
                {isDeleting ? "Tar bort..." : "Ta bort område"}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
